<script lang="ts" setup>
import * as ww from '@wecom/jssdk'
import { onMounted, ref } from 'vue'
import { useLoadingStore, useToastStore } from '@/stores'
import { api } from '@/api'
import { $formatErrorMessage } from '@/helpers'
import { isMobile } from '@/utils'

// 订单状态  0-创建；1-部分支付；2-已支付；3-待发货；4-部分发货；5-已发货；7-确认收货；8-完成；9-取消；99-部分核销
const statuesMap: Record<string, string> = {
  0: '创建',
  1: '部分支付',
  2: '已支付',
  3: '待发货',
  4: '部分发货',
  5: '已发货',
  7: '确认收货',
  8: '完成',
  9: '取消',
  99: '部分核销',
}
const goodStatuesMap: Record<string, string> = { 0: '待核销', 1: '已核销' }

const infiniteRef = ref<HTMLElement>()

const { $showLoading, $hideLoading } = useLoadingStore()
const { $toast } = useToastStore()

const supportScan = ref(false)
onMounted(async () => {
  // refs: https://developer.work.weixin.qq.com/document/path/98132#24229
  ww.register({
    corpId: 'ww3684fd765fcc0201',
    jsApiList: ['scanQRCode'],
    getConfigSignature,
    onConfigFail(error: any) {
      console.warn(error)
      // eslint-disable-next-line no-alert
      window.alert(JSON.stringify(error))
    },
    onConfigSuccess(result) {
      // window.alert(JSON.stringify(result))
      console.warn(result)
      supportScan.value = true
    },
  })

  async function getConfigSignature() {
    const response = await api.post<any, any>(`https://qwer.gdmtest.top:8058/gdm/qywxapi/getWxSign/gdm?url=${window.location.href}`)
    // const timestamp = 1715756217
    // const nonceStr = 'dfasdafdsafafdsafdaf'
    // const signature = 'fab38909ab6b99d1a288501b2e9584ae61d36657'
    const timestamp = response.result.timestamp
    const nonceStr = response.result.nonceStr
    const signature = response.result.sign
    return { timestamp, nonceStr, signature }
  }
})

const orderNo = ref('')
const order = ref()
const mobile = ref('')
const captcha = ref('')

async function onSearch() {
  if (!orderNo.value)
    return $toast('请输入订单编号')

  $showLoading()
  try {
    const { result } = await api.post<any, any>('/gdm/wm/pick/getReplaceOrderList/wmqh', {
      orderNo: orderNo.value,
    })
    order.value = result
    order.value.orderInfoDtlVOList.forEach((good: any) => {
      // eslint-disable-next-line eqeqeq
      good.checked = good.statuses != 0
    })
  }
  catch (error) {
    $toast($formatErrorMessage(error))
  }
  finally {
    $hideLoading()
  }
}

const itemIds = ref<any[]>([])
const modal = ref(false)
async function openModal() {
  itemIds.value = order.value.orderInfoDtlVOList
    // eslint-disable-next-line eqeqeq
    .filter((good: any) => good.statuses == 0)
    .filter((good: any) => good.checked)
    .map((good: any) => good.itemId)

  if (itemIds.value.length === 0)
    return $toast('请选择商品')

  modal.value = true
  mobile.value = ''
  captcha.value = ''
}

const countdown = ref(0)
async function onSendCode() {
  if (!isMobile(mobile.value)) {
    $toast('请输入正确的手机号码')
    return
  }

  try {
    $showLoading()
    await api.post('/gdm/sys/wmsms', {
      mobile: mobile.value,
      smsmode: '2',
    })
    $toast('短信发送成功')
    countdown.value = 60
    startCountdown()
  }
  catch (error: any) {
    $toast($formatErrorMessage(error))
  }
  finally {
    $hideLoading()
  }
}
function startCountdown() {
  if (countdown.value === 0)
    return
  setTimeout(() => {
    countdown.value = countdown.value - 1
    startCountdown()
  }, 1000)
}

async function onConfirm() {
  try {
    $showLoading()
    const { data, message } = await api.post<any, any>('/gdm/wm/pick/employeeCheck/wmqh', {
      captcha: captcha.value,
      phone: mobile.value,
      orderNo: order.value.orderNo,
      itemIds: itemIds.value,
    })
    modal.value = false
    $toast('核销成功')
    onSearch()
  }
  catch (error) {
    $toast($formatErrorMessage(error))
  }
  finally {
    $hideLoading()
  }
}

function onScan() {
  ww.scanQRCode({
    needResult: true,
    scanType: [ww.ScanQRCodeType.qrCode],
    success(result: any) {
      if (!/\d+/.test(result.resultStr))
        return $toast('无效的订单号')

      orderNo.value = result.resultStr
      onSearch()
    },
    fail(error: any) {
      $toast(JSON.stringify(error))
    },
  })
}
</script>

<template>
  <div ref="infiniteRef" class="h-screen overflow-y-scroll bg-white">
    <div class="px-4 py-2 flex items-center gap-4 sticky top-0">
      <input v-model="orderNo" class="px-4 py-2 flex-1 border rounded-full" placeholder="请输入订单编号">
      <button @click="onSearch">
        搜索
      </button>
      <div v-if="supportScan" @click="onScan">
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
          <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 7V6a2 2 0 0 1 2-2h2M4 17v1a2 2 0 0 0 2 2h2m8-16h2a2 2 0 0 1 2 2v1m-4 13h2a2 2 0 0 0 2-2v-1M5 12h14" />
        </svg>
      </div>
    </div>

    <div v-if="order" class="order">
      <div class="order__header">
        <span>日期: {{ order.payTime }}</span>
        <span class="text-2xl text-red">{{ order.pickUpName }}</span>
      </div>
      <div class="order__main">
        <div
          v-for="good in order.orderInfoDtlVOList" :key="good.itemId"
          class="order__good"
        >
          <label class="flex gap-2">
            <!-- eslint-disable-next-line vue/eqeqeq -->
            <input v-model="good.checked" :disabled="good.statuses != 0" type="checkbox">
            <img :src="good.imageUrl" class="order__good-poster">
            <div class="order__good-content">
              <span>{{ good.goodsTitle }}</span>
              <p class="flex gap-4 items-center">
                <span>X {{ Number(good.skuNum) }}</span>
                <span class="text-sm">{{ goodStatuesMap[good.statuses] }}</span>
              </p>
            </div>
          </label>
        </div>
      </div>
      <div class="order__footer">
        <span>订单号: {{ order.orderNo }}</span>
        <span>状态: {{ statuesMap[order.statuses] }}</span>
      </div>
    </div>

    <div class="fixed bottom-10 left-0 w-full px-8">
      <button class="mt-4 w-full px-4 py-2 text-white bg-black rounded" @click="openModal">
        确认收货
      </button>
    </div>

    <BasicModal v-model="modal">
      <div class="px-4 py-8">
        <p class="text-2xl font-bold text-center">
          代收人验证
        </p>
        <div class="mt-4 rounded-lg overflow-hidden border shadow">
          <input v-model="mobile" class="w-full h-10 px-4" maxlength="11" placeholder="请输入代收人手机号">
        </div>
        <div class="mt-4 rounded-lg overflow-hidden border shadow relative">
          <input v-model="captcha" type="text" maxlength="6" class="w-full h-10 px-4" placeholder="验证码">
          <p v-if="countdown === 0" class="text-sm text-#467FF2 lh-10 px-4 absolute right-0 top-0" @click="onSendCode">
            发送验证码
          </p>
          <p v-else class="text-sm text-#999 lh-10 px-4 absolute right-0 top-0">
            {{ countdown }}秒后重新发送
          </p>
        </div>
        <button class="mt-4 w-full px-4 py-2 text-white bg-black rounded" @click="onConfirm">
          确认收货
        </button>
      </div>
    </BasicModal>
  </div>
</template>

<style scoped>
.order {
  padding: 8px 16px;
  background: white;
  margin-top: 10px;
  border-radius: 5px;
}
.order__header {
  display: flex;
  color: #333;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  font-size: 10px;
}
.order__main {
  margin-top: 10px;
}
.order__good {
  margin-bottom: 10px;
  display: flex;
  gap: 15px;
  font-size: 18px;
}
.order__good-poster {
  width: 100px;
  height: 100px;
  border-radius: 5px;
}
.order__good-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.order__footer {
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  font-size: 16px;
}
</style>
